import { useEffect, useState } from "react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell, Legend } from "recharts";
import { Scale, TrendingUp, Wallet, FileCheck2, FileX2, FileClock, Plus, ArrowRight, AlertTriangle, Clock, Archive, Filter } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { financialReport, successReport, installmentAlerts } from "@/lib/api";
import { formatTRY, formatDate, MAHKEME_TURLERI, mahkemeTuruLabel } from "@/lib/constants";
import BackupDialog from "@/components/BackupDialog";
import KarsiVekaletTakip from "@/components/KarsiVekaletTakip";

const COLORS = { basarili: "#10b981", basarisiz: "#ef4444", derdest: "#64748b" };

export default function DashboardView({ cases, onSelectCase, onNewCase }) {
  const [fin, setFin] = useState(null);
  const [succ, setSucc] = useState(null);
  const [alerts, setAlerts] = useState({ gecikmis: [], yakin_vade: [] });
  const [backupOpen, setBackupOpen] = useState(false);
  const [mt, setMt] = useState("tum");

  const load = async () => {
    try {
      const filter = mt === "tum" ? undefined : mt;
      const [f, s, a] = await Promise.all([financialReport(filter), successReport(filter), installmentAlerts(filter)]);
      setFin(f); setSucc(s); setAlerts(a);
    } catch {}
  };
  useEffect(() => { load(); }, [cases.length, mt]); // eslint-disable-line

  const pieData = succ ? [
    { name: "Başarılı", value: succ.basarili, key: "basarili" },
    { name: "Başarısız", value: succ.basarisiz, key: "basarisiz" },
    { name: "Derdest", value: succ.derdest, key: "derdest" },
  ].filter((d) => d.value > 0) : [];

  return (
    <div className="p-6 space-y-6" data-testid="dashboard-view">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <Scale className="w-8 h-8 text-amber-500" />
            <h1 className="font-serif text-4xl font-bold text-slate-100">Dava & Finans Paneli</h1>
          </div>
          <p className="text-slate-400 mt-2 text-sm">Tüm davalarınız, tahsilatlarınız ve başarı istatistikleriniz tek yerde</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 bg-slate-900/60 border border-slate-800 rounded px-2 py-1">
            <Filter className="w-3.5 h-3.5 text-slate-500" />
            <Select value={mt} onValueChange={setMt}>
              <SelectTrigger className="h-7 bg-transparent border-0 text-xs text-slate-200 focus:ring-0 w-[150px]" data-testid="dashboard-mahkeme-turu-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700">
                <SelectItem value="tum">Tüm Mahkemeler</SelectItem>
                {MAHKEME_TURLERI.map((m) => (
                  <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={() => setBackupOpen(true)} variant="outline"
            className="border-slate-700 text-slate-200 hover:bg-slate-800" data-testid="backup-btn">
            <Archive className="w-4 h-4 mr-1" /> Yedekleme
          </Button>
          <Button onClick={onNewCase} className="bg-amber-600 hover:bg-amber-700 text-slate-950">
            <Plus className="w-4 h-4 mr-1" /> Yeni Dava
          </Button>
        </div>
      </div>

      {/* Top stats */}
      <div className="grid md:grid-cols-4 gap-4">
        <KpiCard label="Toplam Dava" value={succ?.toplam ?? cases.length} tone="neutral" icon={<Scale className="w-5 h-5 text-slate-400" />} />
        <KpiCard label="Başarılı (+)" value={succ?.basarili ?? 0} tone="success" icon={<FileCheck2 className="w-5 h-5 text-emerald-500" />} />
        <KpiCard label="Başarısız (-)" value={succ?.basarisiz ?? 0} tone="danger" icon={<FileX2 className="w-5 h-5 text-red-500" />} />
        <KpiCard label="Derdest" value={succ?.derdest ?? 0} tone="muted" icon={<FileClock className="w-5 h-5 text-slate-400" />} />
      </div>

      {/* Financial stats */}
      <div className="grid md:grid-cols-3 gap-4">
        <KpiCard label="Toplam İş Bedeli" value={formatTRY(fin?.total_is_bedeli ?? 0)} tone="gold" icon={<Wallet className="w-5 h-5 text-amber-500" />} />
        <KpiCard label="Toplam Tahsilat" value={formatTRY(fin?.total_tahsilat ?? 0)} tone="success" icon={<TrendingUp className="w-5 h-5 text-emerald-500" />} />
        <KpiCard label="Bekleyen Bakiye" value={formatTRY(fin?.kalan_bakiye ?? 0)} tone="danger" />
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-3 gap-5">
        <Card className="bg-slate-900/60 border-slate-800 lg:col-span-2">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="font-serif text-lg text-slate-100">Nakit Akışı (Aylık)</div>
              <Badge variant="outline" className="border-amber-700 text-amber-500 font-mono text-[10px]">LIVE</Badge>
            </div>
            <div className="h-72">
              {fin && fin.cash_flow?.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={fin.cash_flow}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                    <XAxis dataKey="month" stroke="#64748b" style={{ fontSize: 11 }} />
                    <YAxis stroke="#64748b" style={{ fontSize: 11 }}
                      tickFormatter={(v) => new Intl.NumberFormat("tr-TR", { notation: "compact" }).format(v)} />
                    <Tooltip
                      contentStyle={{ background: "#0f172a", border: "1px solid #334155", borderRadius: 6 }}
                      formatter={(v) => formatTRY(v)}
                    />
                    <Bar dataKey="amount" fill="#D97706" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-slate-500 text-sm">Henüz tahsilat kaydı yok</div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-900/60 border-slate-800">
          <CardContent className="p-5">
            <div className="font-serif text-lg text-slate-100 mb-3">Başarı Dağılımı</div>
            <div className="h-48">
              {pieData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={40} outerRadius={70} paddingAngle={3}>
                      {pieData.map((d) => <Cell key={d.key} fill={COLORS[d.key]} />)}
                    </Pie>
                    <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #334155", borderRadius: 6 }} />
                    <Legend wrapperStyle={{ fontSize: 11, color: "#cbd5e1" }} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-slate-500 text-sm">Veri yok</div>
              )}
            </div>
            <div className="mt-3 pt-3 border-t border-slate-800">
              <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Başarı Oranı</div>
              <div className="text-3xl font-serif text-amber-500 mt-1">%{succ?.basari_orani ?? 0}</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Installment Alerts */}
      {(alerts.gecikmis.length > 0 || alerts.yakin_vade.length > 0) && (
        <div className="grid lg:grid-cols-2 gap-5">
          {alerts.gecikmis.length > 0 && (
            <Card className="bg-red-950/30 border-red-900/60" data-testid="alerts-gecikmis">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                  <div className="font-serif text-lg text-red-400">Gecikmiş Taksitler ({alerts.gecikmis.length})</div>
                </div>
                <div className="divide-y divide-red-900/40 max-h-56 overflow-y-auto">
                  {alerts.gecikmis.slice(0, 8).map((a, i) => (
                    <button key={i} onClick={() => { const c = cases.find((x) => x.id === a.case_id); c && onSelectCase(c); }}
                      className="w-full flex items-center gap-3 py-2 text-left hover:bg-red-950/40 px-2 rounded"
                      data-testid={`alert-overdue-${i}`}>
                      <div className="flex-1 min-w-0">
                        <div className="font-mono text-sm text-slate-200">{a.dava_no}</div>
                        <div className="text-[11px] text-slate-500 truncate">{a.muvekkil || a.mahkeme_adi}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-mono text-sm text-red-400">{formatTRY(a.amount)}</div>
                        <div className="text-[10px] text-red-600">{Math.abs(a.days_remaining)} gün gecikmiş</div>
                      </div>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
          {alerts.yakin_vade.length > 0 && (
            <Card className="bg-amber-950/20 border-amber-900/60" data-testid="alerts-yakin-vade">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Clock className="w-5 h-5 text-amber-500" />
                  <div className="font-serif text-lg text-amber-400">Yaklaşan Vadeler ({alerts.yakin_vade.length})</div>
                </div>
                <div className="divide-y divide-amber-900/40 max-h-56 overflow-y-auto">
                  {alerts.yakin_vade.slice(0, 8).map((a, i) => (
                    <button key={i} onClick={() => { const c = cases.find((x) => x.id === a.case_id); c && onSelectCase(c); }}
                      className="w-full flex items-center gap-3 py-2 text-left hover:bg-amber-950/30 px-2 rounded"
                      data-testid={`alert-upcoming-${i}`}>
                      <div className="flex-1 min-w-0">
                        <div className="font-mono text-sm text-slate-200">{a.dava_no}</div>
                        <div className="text-[11px] text-slate-500 truncate">{a.muvekkil || a.mahkeme_adi}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-mono text-sm text-amber-400">{formatTRY(a.amount)}</div>
                        <div className="text-[10px] text-amber-600">{a.days_remaining} gün kaldı</div>
                      </div>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Karşı Vekalet İcra Takibi */}
      <KarsiVekaletTakip cases={cases} onSelectCase={onSelectCase} mahkemeTuru={mt} refreshKey={cases.length} />

      {/* Recent cases */}
      <Card className="bg-slate-900/60 border-slate-800">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="font-serif text-lg text-slate-100">Son Davalar</div>
          </div>
          {cases.length === 0 ? (
            <div className="text-center py-10 text-slate-500 text-sm">Henüz dava kaydı yok. "Yeni Dava" ile başlayın.</div>
          ) : (
            <div className="divide-y divide-slate-800">
              {cases.slice(0, 7).map((c) => (
                <button key={c.id} onClick={() => onSelectCase(c)}
                  className="w-full flex items-center gap-4 py-3 hover:bg-slate-800/40 px-2 rounded transition text-left"
                  data-testid={`recent-case-${c.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-slate-200">{c.dava_no}</div>
                    <div className="text-xs text-slate-500 truncate">{c.mahkeme_adi}</div>
                  </div>
                  <div className="text-sm text-slate-400 hidden md:block">{c.muvekkil || "—"}</div>
                  <div className="text-right">
                    <div className="font-mono text-amber-500">{formatTRY(c.is_bedeli)}</div>
                    <div className="text-[10px] text-slate-500">{formatDate(c.created_at)}</div>
                  </div>
                  <Badge className={
                    c.result === "basarili" ? "bg-emerald-600 text-white" :
                    c.result === "basarisiz" ? "bg-red-600 text-white" :
                    "bg-slate-700 text-slate-200"
                  }>
                    {c.result === "basarili" ? "+" : c.result === "basarisiz" ? "−" : "•"}
                  </Badge>
                  <ArrowRight className="w-4 h-4 text-slate-500" />
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <BackupDialog open={backupOpen} onOpenChange={setBackupOpen} />
    </div>
  );
}

function KpiCard({ label, value, tone = "neutral", icon }) {
  const tones = {
    neutral: "text-slate-100",
    success: "text-emerald-400",
    danger: "text-red-400",
    gold: "text-amber-500",
    muted: "text-slate-300",
  };
  return (
    <Card className="bg-slate-900/60 border-slate-800">
      <CardContent className="p-5">
        <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-slate-500">
          {icon}{label}
        </div>
        <div className={`mt-2 text-2xl font-serif font-semibold ${tones[tone]}`}>{value}</div>
      </CardContent>
    </Card>
  );
}
